var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-plain-writer.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element";
import { BehaviorController } from "@adornis/chemistry/controllers/behavior-controller";
import { ObservableController } from "@adornis/chemistry/controllers/observable-controller";
import { event } from "@adornis/chemistry/decorators/event";
import { acss } from "@adornis/chemistry/directives/acss";
import { css } from "@adornis/chemistry/directives/css";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { directive, Directive } from "lit/directive.js";
import { ref } from "lit/directives/ref.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { map } from "rxjs/operators";
import { AdornisWriterState } from "./x-writer-state";
var writerMarker = Symbol("partOfAWriter");
var EditorContentDirective = class extends Directive {
  constructor(partInfo) {
    super(partInfo);
    this._element = partInfo.element;
  }
  render(value) {
    if (value !== void 0 && value !== null && this._element.innerHTML !== value)
      return this._element.innerHTML = value;
    return nothing;
  }
};
var editorContent = directive(EditorContentDirective);
var _a;
var ElementsPlainWriter = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this[_a] = true;
    this.disabled = false;
    this["attach-toolbar"] = new BehaviorController(this, false);
    this.placeholder = "";
    this["allow-rich-paste"] = false;
    this["max-chars"] = 0;
    this.value = new BehaviorController(this, "");
    this.empty = new ObservableController(
      this,
      this.value.pipe(
        map((val) => !((this.shadowRoot?.querySelector("#editor") || {}).textContent || (val || "") + "").trim())
      )
    );
    this.error = "";
    this._highlight = new ObservableController(this, AdornisWriterState.highlight);
    this._placeholderTop = 0;
  }
  styles() {
    return { ":host": { display: "flex", flexDirection: "column", cursor: "text", gap: this.spacing.xs } };
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    for (const image of this.renderRoot.querySelectorAll(`${DesignSystem.prefix}-image`)) {
      image.disabled = this.disabled;
    }
  }
  async preValidate() {
    const nodes = this.renderRoot.querySelectorAll(`${DesignSystem.prefix}-image`);
    await Promise.all(Array.from(nodes).map((node) => node.preValidate()));
    this.value.next(this._editor?.innerHTML ?? "");
  }
  render() {
    if (this.disabled) {
      if (!this.value.value)
        return this.placeholderText();
      return html` <div>${unsafeHTML(this.value.value)}</div> `;
    }
    return html`
      <div
        ${acss({
      position: "relative",
      display: "flex",
      flex: "1",
      minWidth: "0",
      flexDirection: "column",
      "#editor": {
        wordWrap: "break-word",
        minHeight: "1em",
        height: "100%",
        overflowY: "auto",
        paddingBottom: "2px",
        "&:focus": { outline: "none" },
        p: { minHeight: "1em" },
        ">*:first-of-type": { marginTop: 0 },
        ">*:last-of-type": { marginBottom: 0 }
      },
      "#placeholder": { position: "absolute" },
      "#toolbarContainer": {
        position: "sticky"
      }
    })}
      >
        ${this.toolbarContainer()}
        <div
          id="editor"
          ${ref((editor) => this._editor = editor)}
          ${editorContent(this.value.value)}
          contenteditable=${this.disabled ? "false" : "true"}
          style=${this._highlight.value ? "border: 3px solid var(--a-writer-highlight-border-color, red); cursor: pointer; background-color: var(--a-writer-background-color, rgba(255,0,0,.5))" : ""}
          @input=${(e) => {
      if (this.disabled)
        return;
      const editor = this.renderRoot.querySelector("#editor");
      this.value.next(editor?.innerHTML || "");
      AdornisWriterState.writerEvents.next(
        new CustomEvent("writer-event", {
          detail: {
            selection: this.getSelection(),
            text: this.value.value,
            originalEvent: e
          }
        })
      );
    }}
          @mouseup=${(e) => {
      if (this.disabled)
        return;
      AdornisWriterState.writerEvents.next(
        new CustomEvent("writer-event", {
          detail: {
            selection: this.getSelection(),
            text: this.value.value,
            originalEvent: e
          }
        })
      );
    }}
          @keyup=${(e) => {
      if (this.disabled)
        return;
      if (e.key.startsWith("Arrow"))
        return;
      AdornisWriterState.writerEvents.next(
        new CustomEvent("writer-event", {
          detail: {
            selection: this.getSelection(),
            text: this.value.value,
            originalEvent: e
          }
        })
      );
    }}
          @keydown=${(e) => {
      if (!this["max-chars"])
        return;
      if (this.value.value.length >= this["max-chars"] && e.code !== "Backspace")
        e.preventDefault();
    }}
          @paste=${(e) => {
      if (this["allow-rich-paste"])
        return;
      e.preventDefault();
      let text = e.clipboardData?.getData("text/plain");
      if (text) {
        if (this["max-chars"])
          text = text.slice(0, this["max-chars"]);
        const selection = this.getSelection();
        if (!selection?.rangeCount)
          return false;
        selection.deleteFromDocument();
        const textNode = document.createTextNode(text);
        selection.getRangeAt(0).insertNode(textNode);
        const range = new Range();
        range.selectNode(textNode);
        range.collapse();
        selection.removeAllRanges();
        selection.addRange(range);
      }
      const editor = this.renderRoot.querySelector("#editor");
      this.value.next(editor?.innerHTML || "");
    }}
          @blur=${() => this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }))}
          @click=${() => {
      if (this.disabled)
        return;
      AdornisWriterState.activeWriter.next(this);
      requestAnimationFrame(() => this._placeholderTop = this._editor?.offsetTop ?? 0);
      document.execCommand("defaultParagraphSeparator", false, "p");
    }}
        >
          <p></p>
        </div>
        ${this.placeholderText()}
      </div>
    `;
  }
  toolbarContainer() {
    return html` <div id="toolbarContainer"></div> `;
  }
  placeholderText() {
    return html`
      <div
        id="placeholder"
        ${css({
      top: this._placeholderTop + "px",
      left: "0",
      color: "var(--placeholder-color, #aaa)",
      pointerEvents: "none",
      display: this.empty.value ? "block" : "none",
      whiteSpace: "nowrap"
    })}
      >
        ${this.placeholder}
      </div>
    `;
  }
  getSelection() {
    return this.renderRoot.getSelection?.() || document.getSelection();
  }
  selectAll() {
    const range = document.createRange();
    if (!this._editor)
      return;
    range.selectNodeContents(this._editor);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }
  focus() {
    this.selectAll();
  }
};
_a = writerMarker;
__decorateClass([
  property({ type: Boolean })
], ElementsPlainWriter.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean })
], ElementsPlainWriter.prototype, "attach-toolbar", 2);
__decorateClass([
  property({ type: String })
], ElementsPlainWriter.prototype, "placeholder", 2);
__decorateClass([
  property({ type: Boolean })
], ElementsPlainWriter.prototype, "allow-rich-paste", 2);
__decorateClass([
  property({ type: Number })
], ElementsPlainWriter.prototype, "max-chars", 2);
__decorateClass([
  event,
  state()
], ElementsPlainWriter.prototype, "value", 2);
__decorateClass([
  property({ type: String })
], ElementsPlainWriter.prototype, "error", 2);
__decorateClass([
  state()
], ElementsPlainWriter.prototype, "_highlight", 2);
__decorateClass([
  state()
], ElementsPlainWriter.prototype, "_placeholderTop", 2);
ElementsPlainWriter = __decorateClass([
  customElement("x-plain-writer")
], ElementsPlainWriter);
export {
  ElementsPlainWriter,
  writerMarker
};
